/**
 * Application Routes
 * Centralized routing configuration with lazy loading
 * Individual flow: Welcome → OTP Login → Entity Type → Aadhaar → Basic → Address
 *                  → PAN → Bank → GSTIN(optional) → Live Photo → MOU Preview → eSign → Success
 */

import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { OnboardingLayout, ProtectedRoute } from '@/components/layout';
import { FullPageLoader } from '@/components/common/Loader';

// Public pages (no auth required)
const WelcomePage = React.lazy(() => import('@/pages/WelcomePage'));
const OTPLoginPage = React.lazy(() => import('@/pages/OTPLoginPage'));
const NotFoundPage = React.lazy(() => import('@/pages/NotFoundPage'));

// Protected onboarding flow
const EntityTypePage = React.lazy(() => import('@/pages/EntityTypePage'));
const AadhaarKYCPage = React.lazy(() => import('@/pages/AadhaarKYCPage'));
const BasicDetailsPage = React.lazy(() => import('@/pages/BasicDetailsPage'));
const AddressPage = React.lazy(() => import('@/pages/AddressPage'));
const PANVerificationPage = React.lazy(() => import('@/pages/PANVerificationPage'));
const BankVerificationPage = React.lazy(() => import('@/pages/BankVerificationPage'));
const GSTINPage = React.lazy(() => import('@/pages/GSTINPage'));
const LivePhotoPage = React.lazy(() => import('@/pages/LivePhotoPage'));
const MOUPreviewPage = React.lazy(() => import('@/pages/MOUPreviewPage'));
const ESignSentPage = React.lazy(() => import('@/pages/ESignSentPage'));
const ESignCallbackPage = React.lazy(() => import('@/pages/ESignCallbackPage'));
const SuccessPage = React.lazy(() => import('@/pages/SuccessPage'));

// Dev-only test harnesses
const ESignTestPage = React.lazy(() => import('@/pages/ESignTestPage'));
const PennyDropTestPage = React.lazy(() => import('@/pages/PennyDropTestPage'));
const GSTTestPage = React.lazy(() => import('@/pages/GSTTestPage'));

const AppRoutes: React.FC = () => {
  return (
    <Suspense fallback={<FullPageLoader text="Loading..." />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/welcome" element={<WelcomePage />} />
        <Route path="/login/otp" element={<Navigate to="/login" replace />} />
        <Route
          path="/login"
          element={
            <ProtectedRoute requireAuth={false}>
              <OTPLoginPage />
            </ProtectedRoute>
          }
        />

        {/* Dev-only test harnesses */}
        <Route path="/dev/esign-test" element={<ESignTestPage />} />
        <Route path="/dev/penny-drop-test" element={<PennyDropTestPage />} />
        <Route path="/dev/gst-test" element={<GSTTestPage />} />

        {/* Protected Routes — Individual Onboarding Flow */}
        <Route
          element={
            <ProtectedRoute>
              <OnboardingLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/entity-type" element={<EntityTypePage />} />
          <Route path="/aadhaar-kyc" element={<AadhaarKYCPage />} />
          <Route path="/basic-details" element={<BasicDetailsPage />} />
          <Route path="/address" element={<AddressPage />} />
          <Route path="/pan-verification" element={<PANVerificationPage />} />
          <Route path="/bank-verification" element={<BankVerificationPage />} />
          <Route path="/gstin" element={<GSTINPage />} />
          <Route path="/live-photo" element={<LivePhotoPage />} />
          <Route path="/mou-preview" element={<MOUPreviewPage />} />
          <Route path="/esign" element={<ESignSentPage />} />
          <Route path="/esign-sent" element={<ESignSentPage />} />
          <Route path="/esign-callback" element={<ESignCallbackPage />} />
          <Route path="/success" element={<SuccessPage />} />
        </Route>

        {/* Redirects */}
        <Route path="/" element={<Navigate to="/login" replace />} />

        {/* 404 - Not Found */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
